import { Alert, Box, Button, Checkbox, Divider, Slider } from '@mui/material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { MatrixCriterion, MatrixCriterionValue } from '../../../../types/advise';
import { weightingSummary } from './FeatureMatrixPanel.calc';

export const WEIGHT_STEP = 5;

export interface CriteriaWeightingProps {
  criteria: MatrixCriterion[];
  canEdit: boolean;
  onChangeCriterion: (criterionId: string, value: MatrixCriterionValue) => void;
  onOpenDetail: () => void;
}

// Selecting a cleared criterion starts it at 0%, never at the weight it had before.
export const selectionValue = (selected: boolean): MatrixCriterionValue =>
  selected ? { selected: true, weight: 0 } : { selected: false };

export const CriteriaWeighting = ({ criteria, canEdit, onChangeCriterion, onOpenDetail }: CriteriaWeightingProps) => {
  const summary = weightingSummary(criteria);

  return (
    <Box data-testid="criteria-weighting">
      <Box className="flex flex-wrap items-center justify-between gap-2 py-3">
        <TextSpan
          size="sm14"
          color="grey6">
          {'Adjust the importance of each evaluation criterion. ' +
            `${summary.selected} of ${summary.total} selected · Total weight: ${summary.totalWeight}%`}
        </TextSpan>
        <Button
          size="small"
          onClick={onOpenDetail}>
          View the Detailed Functionality Matrix →
        </Button>
      </Box>
      {summary.totalWeight !== 100 && (
        <Alert
          severity="warning"
          className="mb-2">
          {`The total weight should be 100%. It is ${summary.totalWeight}% now.`}
        </Alert>
      )}
      <Divider />
      {criteria.map((criterion) => (
        <Box key={criterion.id}>
          <Box className={`flex items-start gap-4 px-6 py-4 ${criterion.selected ? '' : 'opacity-50'}`}>
            <Checkbox
              size="small"
              checked={criterion.selected}
              disabled={!canEdit}
              onChange={(event) => onChangeCriterion(criterion.id, selectionValue(event.target.checked))}
              inputProps={{ 'aria-label': criterion.label }}
              sx={{ mt: 2 }}
            />
            <Box className="flex-1">
              <Box className="flex items-center justify-between">
                <TextSpan
                  size="sm14"
                  weight="semibold"
                  color="grey8">
                  {criterion.label}
                </TextSpan>
                <TextSpan
                  weight="semibold"
                  color="grey8">
                  {criterion.selected ? `${criterion.weight ?? 0}%` : '–'}
                </TextSpan>
              </Box>
              {criterion.description && (
                <TextBlock
                  size="sm14"
                  color="grey6">
                  {criterion.description}
                </TextBlock>
              )}
              <Slider
                size="small"
                value={criterion.selected ? (criterion.weight ?? 0) : 0}
                min={0}
                max={100}
                step={WEIGHT_STEP}
                disabled={!canEdit || !criterion.selected}
                onChange={(_, weight) => onChangeCriterion(criterion.id, { selected: true, weight: weight as number })}
                aria-label={`${criterion.label} weight`}
              />
              {criterion.key_areas.length > 0 && (
                <TextBlock
                  size="xs"
                  color="grey5">
                  {`Key areas: ${criterion.key_areas.join(', ')}`}
                </TextBlock>
              )}
            </Box>
          </Box>
          <Divider />
        </Box>
      ))}
    </Box>
  );
};
